/**
 * Gelişmiş filtre — **uzuv** mertebesi.
 *
 * Tasarım kararları:
 *
 * 1. **Filtreler adres çubuğunda durur** (`?status=FAILED&from=2026-09-01`).
 *    Böylece bir filtre paylaşılabilir, yer imine eklenebilir ve F5 sonrası
 *    kaybolmaz. Panodaki "hatalı faturaları göster" düğmesi de aynı adresi
 *    üretir — iki ayrı mekanizma olmaz.
 *
 * 2. **Hızlı filtreler üstte, ayrıntılı olanlar katlanır.** Günlük kullanımın
 *    %90'ı arama + durum; tarih aralığı ve tutar aralığı nadiren gerekir.
 *    Hepsini birden göstermek ekranı doldurup asıl işi (tabloyu) aşağı iter.
 *
 * 3. **Kaç filtre aktif, rozet olarak görünür.** Katlanmış panelin içinde
 *    unutulmuş bir filtre yüzünden "faturam kayboldu" denmesin.
 *
 * Telefonda tüm alanlar tam genişlik ve alt alta; dokunma hedefleri 44px.
 */

import { useState, type ReactNode } from 'react';
import { Filter, Search, X } from 'lucide-react';
import { Dugme } from './ui/Dugme';
import { Girdi, Secim } from './ui/Girdi';
import { cn } from '../lib/cn';

export interface FiltreDegerleri {
  q: string;
  status: string;
  kind: string;
  direction: string;
  from: string;
  to: string;
  minAmount: string;
  maxAmount: string;
  hasPdf: string;
}

const DURUMLAR = [
  { deger: '', etiket: 'Tüm durumlar' },
  { deger: 'INVALID,FAILED', etiket: 'Dikkat isteyenler' },
  { deger: 'PENDING', etiket: 'Sırada' },
  { deger: 'SENT', etiket: 'Kesildi' },
  { deger: 'GIB_REPORTED', etiket: 'GİB’e raporlandı' },
  { deger: 'DELIVERED', etiket: 'Alıcıya ulaştı' },
  { deger: 'CANCELLED', etiket: 'İptal edildi' },
  { deger: 'REFUNDED', etiket: 'İade edildi' },
];

/** Hızlı erişim: en sık sorulan iki soru tek tıkla. */
const KISAYOLLAR = [
  { etiket: 'Dikkat isteyenler', yama: { status: 'INVALID,FAILED' } },
  { etiket: 'Bugün', yama: { from: bugun(), to: bugun() } },
  { etiket: 'Bu ay', yama: { from: ayinIlkGunu(), to: bugun() } },
  { etiket: 'Belgesi eksik', yama: { hasPdf: 'false' } },
] as const;

/**
 * YEREL takvim günü (YYYY-AA-GG).
 *
 * Ölçülen kusur: `toISOString()` UTC verir. Türkiye UTC+3; gece 00:00–03:00
 * arasında "Bugün" kısayolu BİR ÖNCEKİ günü filtreliyordu, "Bu ay" ayın 1'inde
 * geçen ayı gösteriyordu. Tarayıcının yerel saati kullanıcının takvimidir.
 */
function yerelGun(d = new Date()): string {
  const yil = d.getFullYear();
  const ay = String(d.getMonth() + 1).padStart(2, '0');
  const gun = String(d.getDate()).padStart(2, '0');
  return `${yil}-${ay}-${gun}`;
}

function bugun(): string {
  return yerelGun();
}

function ayinIlkGunu(): string {
  return `${yerelGun().slice(0, 7)}-01`;
}

/** Kaç filtre gerçekten aktif? (arama hariç — o zaten görünür durumda) */
function aktifFiltreSayisi(filtre: FiltreDegerleri): number {
  return (Object.keys(filtre) as (keyof FiltreDegerleri)[]).filter(
    (anahtar) => anahtar !== 'q' && filtre[anahtar] !== ''
  ).length;
}

export interface GelismisFiltreProps {
  deger: FiltreDegerleri;
  /** Tek bir alanı değiştirir; ekran adres çubuğuna yazar. */
  onDegistir: (yama: Partial<FiltreDegerleri>) => void;
  onTemizle: () => void;
  /** Sağa yaslanan ek eylemler (Yenile vb.). */
  eylemler?: ReactNode;
}

export function GelismisFiltre({ deger, onDegistir, onTemizle, eylemler }: GelismisFiltreProps) {
  const aktif = aktifFiltreSayisi(deger);
  // Sayfa açılırken gizli bir filtre varsa panel açık gelsin.
  const [acik, setAcik] = useState(
    Boolean(
      deger.kind ||
      deger.direction ||
      deger.from ||
      deger.to ||
      deger.minAmount ||
      deger.maxAmount ||
      deger.hasPdf
    )
  );
  const [aramaKutusu, setAramaKutusu] = useState(deger.q);

  return (
    <div className="kap uzuv flex flex-col gap-4 p-6">
      {/* ---- Satır 1: arama + durum + eylemler ---- */}
      <form
        className="flex flex-col gap-3 @2xl:flex-row @2xl:items-end"
        onSubmit={(olay) => {
          olay.preventDefault();
          onDegistir({ q: aramaKutusu.trim() });
        }}
      >
        <div className="min-w-0 flex-1">
          <Girdi
            etiket="Ara"
            type="search"
            inputMode="search"
            value={aramaKutusu}
            onChange={(olay) => setAramaKutusu(olay.target.value)}
            placeholder="Fatura no, sipariş no veya alıcı adı"
          />
        </div>

        <div className="@2xl:w-56">
          <Secim
            etiket="Durum"
            value={deger.status}
            onChange={(olay) => onDegistir({ status: olay.target.value })}
          >
            {DURUMLAR.map((secenek) => (
              <option key={secenek.deger} value={secenek.deger}>
                {secenek.etiket}
              </option>
            ))}
          </Secim>
        </div>

        <div className="flex items-center gap-2">
          <Dugme
            type="submit"
            gorunum="birincil"
            ikon={<Search size={15} />}
            className="flex-1 @2xl:flex-none"
          >
            Ara
          </Dugme>
          <Dugme
            gorunum="ikincil"
            ikon={<Filter size={15} />}
            aria-expanded={acik}
            onClick={() => setAcik((a) => !a)}
            className="flex-1 @2xl:flex-none"
          >
            Filtreler
            {aktif > 0 && (
              <span className="rakam ml-1 rounded-full bg-[var(--accent)] px-1.5 text-xs text-[var(--accent-ink)]">
                {aktif}
              </span>
            )}
          </Dugme>
          {eylemler}
        </div>
      </form>

      {/* ---- Kısayollar ---- */}
      <div className="flex flex-wrap gap-2">
        {KISAYOLLAR.map((kisayol) => {
          const secili = (Object.entries(kisayol.yama) as [keyof FiltreDegerleri, string][]).every(
            ([anahtar, v]) => deger[anahtar] === v
          );
          return (
            <button
              key={kisayol.etiket}
              type="button"
              onClick={() => onDegistir(secili ? bosla(kisayol.yama) : kisayol.yama)}
              className={cn(
                'rounded-full border px-3 py-1.5 text-xs font-medium transition-colors',
                secili
                  ? 'border-[var(--accent)] bg-[var(--accent-soft)] text-[var(--accent)]'
                  : 'border-[var(--line-strong)] bg-[var(--surface)] text-[var(--ink-muted)] hover:bg-[var(--surface-hover)] hover:text-[var(--ink)]'
              )}
            >
              {kisayol.etiket}
            </button>
          );
        })}
      </div>

      {/* ---- Katlanır ayrıntılı filtreler ---- */}
      {acik && (
        <div className="grid grid-cols-1 gap-3 border-t border-[var(--line)] pt-4 @xl:grid-cols-2 @4xl:grid-cols-3">
          <Secim
            etiket="Belge tipi"
            value={deger.kind}
            onChange={(o) => onDegistir({ kind: o.target.value })}
          >
            <option value="">Tümü</option>
            <option value="EARCHIVE">e-Arşiv</option>
            <option value="EINVOICE">e-Fatura</option>
          </Secim>

          <Secim
            etiket="Fatura yönü"
            value={deger.direction}
            onChange={(o) => onDegistir({ direction: o.target.value })}
          >
            <option value="">Tümü</option>
            <option value="SALES">Satış</option>
            <option value="CREDIT_NOTE">İade</option>
          </Secim>

          <Secim
            etiket="Belge durumu"
            value={deger.hasPdf}
            onChange={(o) => onDegistir({ hasPdf: o.target.value })}
          >
            <option value="">Farketmez</option>
            <option value="true">PDF hazır</option>
            <option value="false">PDF henüz yok</option>
          </Secim>

          <Girdi
            etiket="Başlangıç tarihi"
            type="date"
            value={deger.from}
            max={deger.to || undefined}
            onChange={(o) => onDegistir({ from: o.target.value })}
          />
          <Girdi
            etiket="Bitiş tarihi"
            type="date"
            value={deger.to}
            min={deger.from || undefined}
            onChange={(o) => onDegistir({ to: o.target.value })}
          />

          <div className="grid grid-cols-2 gap-3">
            <Girdi
              etiket="En az tutar"
              type="number"
              inputMode="decimal"
              min={0}
              step="0.01"
              placeholder="0,00"
              value={deger.minAmount}
              onChange={(o) => onDegistir({ minAmount: o.target.value })}
            />
            <Girdi
              etiket="En çok tutar"
              type="number"
              inputMode="decimal"
              min={0}
              step="0.01"
              placeholder="—"
              value={deger.maxAmount}
              onChange={(o) => onDegistir({ maxAmount: o.target.value })}
            />
          </div>
        </div>
      )}

      {(aktif > 0 || deger.q) && (
        <div className="flex border-t border-[var(--line)] pt-3">
          <Dugme
            gorunum="sessiz"
            boy="sm"
            ikon={<X size={14} />}
            onClick={() => {
              setAramaKutusu('');
              onTemizle();
            }}
          >
            Filtreleri temizle
          </Dugme>
        </div>
      )}
    </div>
  );
}

/** Bir kısayolun uyguladığı alanları geri boşaltır (ikinci tıklamada kapansın). */
function bosla(yama: Readonly<Partial<FiltreDegerleri>>): Partial<FiltreDegerleri> {
  const sonuc: Partial<FiltreDegerleri> = {};
  for (const anahtar of Object.keys(yama) as (keyof FiltreDegerleri)[]) sonuc[anahtar] = '';
  return sonuc;
}
